import ToleranceInput from '../inputs/tolerance';
import FilterConstants from '../../filter-constants';
import { isNullOrUndefined } from '../../utils/common';
import type IndexModel from '../index';
import ComponentsAdvisor from '../../models/advisors/component-selection';

import IndividualStageInput from '../inputs/component-selection/individual-stage';

const _defaults = {
  capacitorTolerance: FilterConstants.capacitorTolerance.five,
  resistorTolerance: FilterConstants.resistorTolerance.one,
  inductorTolerance: FilterConstants.inductorTolerance.five,
  opAmpGbwTolerance: FilterConstants.gbwTolerance.twenty,
  capacitorPreferredSeries: FilterConstants.eSeries.E24,
  resistorPreferredSeries: FilterConstants.eSeries.E96,
  inductorPreferredSeries: FilterConstants.eSeries.E12,
};

////////////////////////////////////////////////////////////////////////////////////
// tolerance - Advisor that runs when user goes into the tolerances tab           //
////////////////////////////////////////////////////////////////////////////////////

export default class ToleranceAdvisor {
  model: IndexModel;
  componentAdvisor: ComponentsAdvisor;

  constructor(model: IndexModel) {
    this.model = model;
    this.componentAdvisor = new ComponentsAdvisor(this.model);
  }

  ////////////////////////////////////////////////////////////////////////////////
  // User Functions                                                             //
  ////////////////////////////////////////////////////////////////////////////////

  // Creates the tolerance input model
  // Inputs:
  //   - data : input model values, if this is null, defaults are used
  // Returns: tolerance input model
  generate(data: ToleranceInput): ToleranceInput {
    return new ToleranceInput(data ? data : _defaults);
  }

  // Creates a Copy of the Model
  // Inputs:
  //   - sourceModel : model to be copied from
  // Returns: result - a newly created input model with values copied from the source model
  clone(sourceModel: ToleranceInput): ToleranceInput {
    const result = new ToleranceInput();

    result.capacitorToleranceList = sourceModel.capacitorToleranceList;
    result.resistorToleranceList = sourceModel.resistorToleranceList;
    result.inductorToleranceList = sourceModel.inductorToleranceList;
    result.opAmpGbwToleranceList = sourceModel.opAmpGbwToleranceList;
    result.capacitorPreferredSeriesList = sourceModel.capacitorPreferredSeriesList;
    result.resistorPreferredSeriesList = sourceModel.resistorPreferredSeriesList;
    result.inductorPreferredSeriesList = sourceModel.inductorPreferredSeriesList;
    result.capacitorTolerance = sourceModel.capacitorTolerance;
    result.resistorTolerance = sourceModel.resistorTolerance;
    result.inductorTolerance = sourceModel.inductorTolerance;
    result.opAmpGbwTolerance = sourceModel.opAmpGbwTolerance;
    result.capacitorPreferredSeries = sourceModel.capacitorPreferredSeries;
    result.resistorPreferredSeries = sourceModel.resistorPreferredSeries;
    result.inductorPreferredSeries = sourceModel.inductorPreferredSeries;
    return result;
  }

  // Checks if any of the stages has an inductor implementation
  // Returns: true if one or more stages has an inductor implementation, otherwise false
  hasInductors(): boolean {
    return this.model.inputs.components.individualStageInputs.some((item: IndividualStageInput) => {
      return item.implementation === FilterConstants.implementationType.inductor;
    });
  }

  // Validate Tolerance Inputs
  // Currently we do not validate the dropdown inputs in tolerances tab
  // The two parameters in the validate function are not used, however, we need to keep them
  // consistent as we are using typescript as this is called from the controller.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  validate(_toleranceInput: ToleranceInput, _nameArrayOfFailedFields: Array<string>): Array<ValidationError | null> {
    return [];
  }

  // Checks if the tab has restrictions that cause it to disable the succeeding steps in the wizard
  // Returns: ideals - true if there is one of more tolerances or preferred series are set to "ideal"
  hasRestrictions(): boolean {
    return this.hasIdealValues();
  }

  // Checks if the one or more of the tolerance dropdown values are set to "ideal"
  // Returns: result - true if there is one of more ideal values, otherwise false
  hasIdealValues(): boolean {
    let result = false;
    if (!isNullOrUndefined(this.model.inputs.tolerances)) {
      result =
        this.model.inputs.tolerances.capacitorTolerance === 'ideal' ||
        this.model.inputs.tolerances.resistorTolerance === 'ideal' ||
        this.model.inputs.tolerances.opAmpGbwTolerance === 'ideal' ||
        this.model.inputs.tolerances.capacitorPreferredSeries === 'ideal' ||
        this.model.inputs.tolerances.resistorPreferredSeries === 'ideal' ||
        (this.hasInductors() &&
          (this.model.inputs.tolerances.inductorTolerance === 'ideal' ||
            this.model.inputs.tolerances.inductorPreferredSeries === 'ideal'));
    }
    return result;
  }

  updateComponentsModel(toleranceModel: ToleranceInput): void {
    this.model.inputs.tolerances.resistorTolerance = toleranceModel.resistorTolerance;

    const workingModel = this.model.inputs.components;
    const sliderValues = (this.componentAdvisor as ComponentsAdvisor).getSliderValues(workingModel);
    for (let i = 0; i < workingModel.individualStageInputs.length; i++) {
      const individualStageInput = workingModel.individualStageInputs[i];
      individualStageInput.gainRatio = sliderValues.gainValues[i];
    }

    this.model.inputs.components = workingModel;
  }
}
